import { PhoneProvider } from '@compass/client/types/phone_number';

import { TelnyxVoiceProvider } from './telnyx';
import { TwilioVoiceProvider } from './twilio';
import type { VoiceProviderService } from './types';

/**
 * One service per carrier for the lifetime of the tab, keyed by carrier.
 *
 * A map rather than a conditional: the previous form routed anything that was not
 * Telnyx to the Twilio adapter, so a carrier with no adapter would have dialled on
 * the wrong one and had its token rejected with nothing to point at.
 *
 * Shared instances matter: TwilioCallButton polls "is a call in progress"
 * independently of the dialpad, and both have to be looking at the same session.
 */
const services = new Map<PhoneProvider, VoiceProviderService>([
  [PhoneProvider.PHONE_PROVIDER_TWILIO, new TwilioVoiceProvider()],
  [PhoneProvider.PHONE_PROVIDER_TELNYX, new TelnyxVoiceProvider()],
]);

/**
 * The adapter for a given carrier.
 *
 * @public used from voice/__tests__/index.test.ts to verify routing directly
 * against the map this module owns, rather than only through setActiveVoiceProvider.
 */
export function voiceServiceFor(provider: PhoneProvider): VoiceProviderService {
  const service = services.get(provider);
  if (!service) throw new Error(`No adapter for provider ${provider}`);
  return service;
}

/**
 * The carrier serving the current call.
 *
 * Held here rather than passed to every call site because the dialer reaches for
 * the service in a dozen places — hangUp, sendDigits, toggleMute, destroy — and
 * every one of them has to reach the carrier that actually placed the call.
 * Defaulting to Twilio preserves today's behaviour for any path that never sets
 * it.
 */
let active: VoiceProviderService = services.get(PhoneProvider.PHONE_PROVIDER_TWILIO)!;

/**
 * Points subsequent voiceService() calls at the carrier that issued the token.
 *
 * Called once per call, right after the token is minted, because the token
 * response is the authoritative statement of which carrier is serving it — a
 * token only works against the carrier that issued it.
 */
export function setActiveVoiceProvider(provider: PhoneProvider | undefined): VoiceProviderService {
  // undefined keeps today's behaviour for any path that never sets a carrier.
  active =
    provider === undefined
      ? services.get(PhoneProvider.PHONE_PROVIDER_TWILIO)!
      : voiceServiceFor(provider);
  return active;
}

/** The voice service for the current call. */
export function voiceService(): VoiceProviderService {
  return active;
}

/**
 * Whether any carrier currently has a call up.
 *
 * Checks both rather than only the active one, so a stale active pointer cannot
 * hide a live call from the guards that stop a second call being placed.
 */
export function isAnyCallInProgress(): boolean {
  return [...services.values()].some(service => service.isCallInProgress());
}

/** Whether this browser can run the current carrier's voice SDK. */
export function isVoiceSupported(): boolean {
  return active.isSupported();
}
